import React, { useCallback, useMemo } from 'react';
import Chart from 'react-apexcharts';
import clsx from 'clsx';
import { contextMenu } from 'react-contexify';

import { getChartBaseColors, getChartFontColor, getChartGridColor } from '../../../../utils/chart';
import LegendItem from '../../legend/LegendItem';
import VisitWebsiteContextMenu from '../../../visit-website-context-menu/VisitWebsiteContextMenu';

import scss from './RadialBar.module.scss';

const RADIAL_BAR_CONTEXT_MENU = 'radial-bar/VisitWebsiteContextMenu';

const RadialBar = ({ domain, score, trend, inFocus, theme, label, myCompany }) => {
  const values = useMemo(() => [score, trend], [score, trend]);

  const fontColor = getChartFontColor(theme);
  const gridColor = getChartGridColor(theme);
  const BASE_COLORS = getChartBaseColors(theme);

  const { series, options } = useMemo(
    () => ({
      series: values.map((v) => Math.abs(v)),
      options: {
        chart: {
          height: 390,
          type: 'radialBar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          radialBar: {
            startAngle: 0,
            endAngle: 360,
            hollow: {
              margin: 5,
              size: '30%',
              background: 'transparent',
              image: undefined,
            },
            track: {
              background: gridColor,
            },
            dataLabels: {
              name: {
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'Inter',
                offsetY: -4,
              },
              value: {
                fontSize: '14px',
                fontWeight: 600,
                color: fontColor,
                fontFamily: 'Inter',
                offsetY: -2,
                formatter: function (val) {
                  if (val === String(Math.abs(score))) {
                    return (score < 0 ? '-' + val : val) + '%';
                  }
                  if (val === String(Math.abs(trend))) {
                    return (trend < 0 ? '-' + val : val) + '%';
                  }
                  return val;
                },
              },
              total: {
                show: true,
                label: 'Score',
                fontSize: '14px',
                fontWeight: 600,
                color: fontColor,
                fontFamily: 'Inter',
                formatter: () => {
                  return score + '%';
                },
              },
            },
          },
        },
        fill: {
          colors: [
            (() => (score < 0 ? BASE_COLORS.RED : BASE_COLORS.BLUE))(),
            (() => (trend < 0 ? BASE_COLORS.RED : BASE_COLORS.GREEN))(),
          ],
        },
        labels: ['Score', 'Trend'],
      },
    }),
    [score, trend, values, fontColor, gridColor, BASE_COLORS],
  );

  const displayContextMenu = useCallback(
    (event) => {
      if (domain) {
        contextMenu.show({
          id: RADIAL_BAR_CONTEXT_MENU,
          event,
          props: {
            domain,
          },
        });
      }
    },
    [domain],
  );

  return (
    <div
      className={clsx(scss.chart, 'no-select', score < 0 && scss.negativeScore, trend < 0 && scss.negativeTrend)}
      onContextMenu={displayContextMenu}>
      <div style={{ maxHeight: '179px' }}>
        <Chart series={series} options={options} type='radialBar' width={160} height={200} />
      </div>
      <LegendItem title={label || domain} domain={domain} inFocus={inFocus} myCompany={myCompany} />
      <VisitWebsiteContextMenu id={RADIAL_BAR_CONTEXT_MENU} />
    </div>
  );
};

export default RadialBar;
